/**
 * Pure, React-free helpers for the Phase-4 hierarchy-based supervisor approval
 * UI (plan-v9). Everything here is deterministic and side-effect-free so it can
 * be unit-tested directly (web-app has no vitest harness yet — see the package
 * `test` script — so these are factored out to stay cleanly testable when one is
 * added, per the Phase-4 build constraints).
 *
 * SECURITY / PII: these helpers only ever touch org-chart fields + email that the
 * API already returns (display name, ns ids, supervisor, email). No PIN, no
 * secret, no extra PII is derived or surfaced here.
 */

import type {
  ApprovedVia,
  CallerLink,
  EmployeeSupervisorMapping,
  ListSessionsScope,
  SetUserEmployeeLinkRequest,
} from '@worklogix/shared';

/** Non-blocking banner copy for a Manager with no resolved employee link. */
export const NOT_LINKED_BANNER =
  "You're not linked to an employee record — contact an admin. You can view time, " +
  'but you have no reports to approve until your login is linked.';

/**
 * True when the not-linked banner should be shown. Only a NON-admin caller is
 * ever stranded by being unlinked (an Admin approves via override regardless), so
 * we suppress the banner for admins even when their own link is unresolved.
 */
export function shouldShowNotLinkedBanner(link: CallerLink | null, isAdmin: boolean): boolean {
  if (link === null || isAdmin) {
    return false;
  }
  return link.unlinked;
}

/**
 * The initial `GET /time/sessions` scope for a caller. An Admin lands on `all`
 * (the whole tenant) with the toggle available to narrow to `mine`; a non-admin
 * Manager sends NO scope — the server always constrains them to their report set
 * regardless of any requested value, so the client never asserts a scope.
 */
export function initialScope(isAdmin: boolean, isViewer: boolean): ListSessionsScope | undefined {
  return isAdmin || isViewer ? 'all' : undefined;
}

/**
 * Resolve the scope actually sent to the API. An Admin's chosen scope passes
 * through; View Only always reads the whole tenant (`all`, read-only); a Manager
 * can never widen beyond their reports, so their scope is dropped (undefined).
 */
export function effectiveScope(
  isAdmin: boolean,
  isViewer: boolean,
  chosen: ListSessionsScope
): ListSessionsScope | undefined {
  if (isAdmin) {
    return chosen;
  }
  return isViewer ? 'all' : undefined;
}

/** Human label for the current session scope indicator. */
export function scopeIndicatorLabel(scope: ListSessionsScope): string {
  return scope === 'all' ? 'Showing: All employees' : 'Showing: My reports';
}

/**
 * Whether the caller may (in the UI) approve a given visible session row. The
 * SERVER is always the authority; this only mirrors it so we don't dangle an
 * action that will 403:
 *   - Admin → always (override-approve anyone);
 *   - linked Manager → yes — the server only ever returns rows in their report
 *     set, so every visible row is approvable;
 *   - unlinked non-admin → never (they supervise nobody).
 * Posted / already-approved gating is handled separately by the caller.
 */
export function canApproveInUi(
  isAdmin: boolean,
  isViewer: boolean,
  link: CallerLink | null
): boolean {
  if (isViewer) {
    return false;
  }
  if (isAdmin) {
    return true;
  }
  return link !== null && !link.unlinked;
}

/** Friendly, non-PII explanation for why an employee needs an Admin override. */
export function needsAdminReasonText(
  reason: EmployeeSupervisorMapping['needsAdminReason']
): string {
  switch (reason) {
    case 'no_supervisor':
      return 'No supervisor assigned in NetSuite.';
    case 'system_supervisor_id':
      return 'Supervisor is a NetSuite system/position reference, not a person.';
    case 'supervisor_not_wlx_user':
      return "Supervisor isn't a WorkLogix user, so they can't sign in to approve.";
    case 'cycle':
      return 'The supervisor chain loops back on itself (a cycle was detected).';
    case undefined:
      return '';
    default:
      return '';
  }
}

/** Short badge label + tooltip for a session's approval provenance. */
export interface ApprovedViaBadge {
  label: string;
  tooltip: string;
  /** Fluent Badge color. */
  color: 'success' | 'brand';
}

/** Map an {@link ApprovedVia} to a subtle badge shown on approved rows. */
export function approvedViaBadge(via: ApprovedVia): ApprovedViaBadge {
  if (via === 'admin_override') {
    return {
      label: 'Admin override',
      tooltip: 'Approved by an admin outside the employee’s supervisor chain.',
      color: 'brand',
    };
  }
  return {
    label: 'Supervisor',
    tooltip: 'Approved by the employee’s supervisor.',
    color: 'success',
  };
}

/**
 * The set of `employeeId`s whose sessions can only be cleared by an Admin
 * override (no usable supervisor / cycle). Drives the "needs admin approval"
 * badge on session rows. Sourced from the admin mapping data, so it is only
 * populated for an Admin (the mapping endpoint is Admin-only).
 */
export function needsAdminEmployeeIds(mappings: readonly EmployeeSupervisorMapping[]): Set<string> {
  const ids = new Set<string>();
  for (const m of mappings) {
    if (m.needsAdminApproval) {
      ids.add(m.employeeId);
    }
  }
  return ids;
}

/**
 * The set of `employeeId`s that share a (case-insensitive) email with at least
 * one OTHER employee — the create-vs-annotate duplicate risk (a manual PIN row
 * vs a sync-created org-chart node for the same person). Surfaced in the mapping
 * view so an admin can collapse them by setting `ns_employee_id`; NEVER
 * auto-merged. Blank/null emails are ignored (not a duplicate signal).
 */
export function duplicateEmailEmployeeIds(
  // Structural, not `EmployeeSupervisorMapping[]`: the Employees roster computes
  // this from the employee list itself (which always loads) rather than from the
  // Admin-only mapping endpoint, so duplicates stay flagged if that endpoint fails.
  mappings: readonly { employeeId: string; email: string | null }[]
): Set<string> {
  const byEmail = new Map<string, string[]>();
  for (const m of mappings) {
    const email = m.email?.trim().toLowerCase();
    if (email === undefined || email === '') {
      continue;
    }
    const bucket = byEmail.get(email) ?? [];
    bucket.push(m.employeeId);
    byEmail.set(email, bucket);
  }
  const dupes = new Set<string>();
  for (const bucket of byEmail.values()) {
    if (bucket.length > 1) {
      for (const id of bucket) {
        dupes.add(id);
      }
    }
  }
  return dupes;
}

/** Display label for a mapping's resolved supervisor (name → id → em-dash). */
export function supervisorLabel(m: EmployeeSupervisorMapping): string {
  if (m.supervisorDisplayName !== null && m.supervisorDisplayName !== '') {
    return m.supervisorDisplayName;
  }
  if (m.nsSupervisorId !== null && m.nsSupervisorId !== '') {
    return `NS #${m.nsSupervisorId} (not a WorkLogix user)`;
  }
  return '—';
}

/**
 * Build the `PATCH /users/:id/employee-link` body from raw text input. A blank
 * (or whitespace-only) value CLEARS the link (null); anything else is trimmed and
 * sent verbatim as the NS employee id.
 */
export function linkPayload(raw: string): SetUserEmployeeLinkRequest {
  const trimmed = raw.trim();
  return { nsEmployeeId: trimmed === '' ? null : trimmed };
}
